import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { AgentsCategoryService } from "../../../services/Agents/AgentsCategoryService";
import { AgentsCategoryModel } from "../../../models/Agents/AgentsCategory/AgentsCategoryModel";

interface ContextProps {
  category: AgentsCategoryModel | null;
  setCategory: Dispatch<SetStateAction<AgentsCategoryModel | null>>;
  categories: AgentsCategoryModel[];
  categoriesList: AgentsCategoryModel[];
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  error: string;
  categoriesOptions: any[];
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleListCategories: () => void;
  setCategoriesList: Dispatch<SetStateAction<any>>;
  handleReactivate: (id: number) => Promise<boolean>;
}

export const AgentsCategoriesContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface AgentsCategoriesProviderProps {
  children: ReactNode;
}

export const AgentsCategoriesProvider = ({
  children,
}: AgentsCategoriesProviderProps) => {
  const [category, setCategory] = useState<AgentsCategoryModel | null>(null);
  const [categories, setCategories] = useState<AgentsCategoryModel[]>([]);
  const [categoriesList, setCategoriesList] = useState<AgentsCategoryModel[]>(
    [],
  );
  const [categoriesOptions, setCategoriesOptions] = useState<any[]>([]);
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new AgentsCategoryService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (
      !params ||
      (!!params?.withPaginate &&
        !location.pathname.includes("/articles/categories/list"))
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setCategories(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setCategories([]);
      setPages({});
      setError("Não foi possível carregar as categorias.");
    }
  }, [params, location.pathname]);

  const handleListCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list({
        filter: { only_active: true },
        withPaginate: false,
      });
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setCategoriesList(_Response?.items);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setCategories([]);
      setPages({});
      setError("Não foi possível carregar as categorias.");
    }
  }, []);

  const handleFetch = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "top-right",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar a categoria");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar a categoria",
      );
      return false;
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar a categoria",
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  useEffect(() => {
    setCategoriesOptions(
      categories?.map((item: AgentsCategoryModel) => ({
        label: item.name,
        value: item.id,
      })),
    );
  }, [categories]);

  return (
    <AgentsCategoriesContext.Provider
      value={{
        category,
        setCategory,
        categories,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        setIsLoading,
        isLoadingModal,
        error,
        categoriesOptions,
        categoriesList,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleListCategories,
        setCategoriesList,
        handleReactivate,
      }}
    >
      {children}
    </AgentsCategoriesContext.Provider>
  );
};

export const useAgentsCategories = () => useContext(AgentsCategoriesContext);
